import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { siteConfig, quickLinks, categories, articles, faqs } from '@/mocks/siteData';

function QuickLinkCard({ link }: { link: typeof quickLinks[0] }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className="group block p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${link.icon} text-lg`}></i>
        </div>
        <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${
          link.badge === '推荐' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
        }`}>
          {link.badge}
        </span>
      </div>
      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1">
        {link.name}
      </h3>
      <p className="text-foreground-600 text-sm leading-relaxed mb-3">
        {link.description}
      </p>
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1 text-accent-600">
          <i className="ri-flashlight-line"></i>
          {link.speed}
        </span>
        <span className="text-primary-600 group-hover:translate-x-1 transition-transform duration-200 flex items-center gap-1">
          立即访问 <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </a>
  );
}

function CategoryCard({ category }: { category: typeof categories[0] }) {
  return (
    <Link
      to={`/category/${category.slug}`}
      className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 flex items-start gap-4"
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
        <i className={`${category.icon} text-xl`}></i>
      </div>
      <div className="min-w-0">
        <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1 whitespace-nowrap">
          {category.name}
        </h3>
        <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
          {category.description}
        </p>
        <span className="inline-block mt-2 text-xs text-accent-600">
          {category.count} 篇文章
        </span>
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.thumbnail}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          width="800"
          height="480"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500">
          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
            {article.category}
          </span>
          <span>{article.date}</span>
          <span>{article.readTime}</span>
        </div>
        <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2">
          {article.summary}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {article.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function FaqAccordion({ faq, isOpen, onToggle }: { faq: typeof faqs[0]; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border border-background-200/70 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left bg-background-50 hover:bg-background-100 transition-colors"
      >
        <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
        <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-foreground-500 text-lg flex-shrink-0 transition-transform duration-300`}></i>
      </button>
      {isOpen && (
        <div className="px-5 pb-5">
          <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});

  const toggleFaq = (index: number) => {
    setOpenFaqs((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteConfig.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.brand.name,
    url: siteConfig.url,
    description: siteConfig.brand.intro,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.brand.contact.phone,
      email: siteConfig.brand.contact.email,
      contactType: 'customer service',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <PageMeta
        title={`${siteConfig.name} — 安全稳定一秒直达`}
        description={siteConfig.description}
        canonical={siteConfig.url}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20geometric%20pattern%20with%20soft%20flowing%20lines%2C%20minimalist%20modern%20design%2C%20cream%20and%20amber%20gradients%2C%20clean%20professional%20background%2C%20subtle%20texture&width=1600&height=900&seq=hero-bg-main&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
              width="1600"
              height="900"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>
          </div>
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-xs mb-6">
              <i className="ri-verified-badge-line"></i>
              官方认证入口导航 · 安全可靠
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-6 leading-tight">
              17c com入口 · 官方直达通道
            </h1>
            <p className="text-background-50/85 text-sm md:text-base leading-relaxed mb-8 max-w-[650px] mx-auto">
              {siteConfig.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={quickLinks[0].url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-link mr-2"></i>立即访问官方主入口
              </a>
              <Link
                to="/category/access-guide"
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 font-medium text-sm hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-guide-line mr-2"></i>查看访问指南
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                官方直达入口
              </h2>
              <p className="text-foreground-600 text-sm max-w-[500px] mx-auto">
                多个入口地址，确保您始终可以稳定访问17c com平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {quickLinks.map((link) => (
                <QuickLinkCard key={link.id} link={link} />
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                内容分类导航
              </h2>
              <p className="text-foreground-600 text-sm max-w-[500px] mx-auto">
                按需浏览，快速定位您需要的帮助内容
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <CategoryCard key={cat.slug} category={cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  精选文章
                </h2>
                <p className="text-foreground-600 text-sm">
                  最新的17c com入口使用教程与资讯
                </p>
              </div>
              <Link
                to="/category/latest-news"
                className="flex items-center gap-1 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 6).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-foreground-600 text-sm max-w-[500px] mx-auto">
                关于17c com入口的常见问题，这里都有答案
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <FaqAccordion
                  key={index}
                  faq={faq}
                  isOpen={!!openFaqs[index]}
                  onToggle={() => toggleFaq(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* About Preview / CTA */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="rounded-2xl overflow-hidden relative">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20flowing%20gradient%20with%20soft%20curves%2C%20minimalist%20geometric%20overlay%2C%20cream%20and%20amber%20tones%2C%20professional%20corporate%20style%2C%20clean%20modern%20design&width=1400&height=500&seq=about-cta-bg&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover"
                  width="1400"
                  height="500"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-900/60 to-primary-800/40"></div>
              </div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20">
                <div className="max-w-[600px]">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                    关于我们
                  </h2>
                  <p className="text-background-50/85 text-sm leading-relaxed mb-2">
                    {siteConfig.brand.intro}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-8">
                    {siteConfig.brand.stats.map((stat) => (
                      <div key={stat.label} className="text-center">
                        <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                          {stat.value}
                        </div>
                        <div className="text-background-50/70 text-xs mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-50 text-primary-700 font-medium text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap"
                  >
                    了解更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}